import {Injectable} from '@angular/core';
import {AbstractControl, FormArray, FormGroup} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {StepperBuilderService} from '../../../avisDePub/creer/services/stepper.builder.service';
import {EformControl} from '../../../shared/models/eformControl';

/**
 * PRB0054349 : TED rejette un avis rectificatif eForms dont la section « Modifier » ne précise aucune section
 * modifiée (BT-13716-notice) : sans élément efac:Change, l'avis est traité comme un second avis initial.
 * Le SDK ne rend pas ce champ obligatoire ; le métier impose d'en renseigner au moins un sur tout avis rectificatif.
 */
@Injectable({
  providedIn: 'root'
})
export class EformChangeSectionService {

  static readonly CHANGED_NOTICE_FIELD = 'BT-758-notice';
  static readonly CHANGED_SECTION_FIELD = 'BT-13716-notice';
  static readonly CHANGE_REASON_FIELD = 'BT-140-notice';

  constructor(private stepperBuilderService: StepperBuilderService,
              private translateService: TranslateService) {
  }

  /**
   * Ajoute une erreur bloquante, localisée sur la section modifiée, si l'avis est un rectificatif
   * (BT-758-notice renseigné) sans aucune section modifiée renseignée.
   * @param form formulaire du stepper (contient le groupe 'eforms')
   */
  validate(form: FormGroup): void {
    const eforms = form?.get('eforms');
    if (!eforms) {
      return;
    }
    const changedNoticeControls = this.findValueControls(eforms, EformChangeSectionService.CHANGED_NOTICE_FIELD);
    if (!changedNoticeControls.some(control => this.hasValue(control.value))) {
      return;
    }
    const changedSectionControls = this.findValueControls(eforms, EformChangeSectionService.CHANGED_SECTION_FIELD);
    if (changedSectionControls.some(control => this.hasValue(control.value))) {
      return;
    }
    const location = this.getLocation([
      ...changedSectionControls,
      ...this.findValueControls(eforms, EformChangeSectionService.CHANGE_REASON_FIELD),
      ...changedNoticeControls
    ]);
    if (!location) {
      return;
    }
    this.stepperBuilderService.addStepFieldsOnErrors(location.split('|')[0], location, this.getMessage());
  }

  /**
   * Retourne les contrôles 'val' des champs eForms d'identifiant fieldId (le groupe parent porte l'inputId du champ).
   */
  private findValueControls(control: AbstractControl, fieldId: string): AbstractControl[] {
    let children: AbstractControl[];
    if (control instanceof FormArray) {
      children = control.controls;
    } else if (control instanceof FormGroup) {
      const inputIds = [control.get('inputId')?.value, ...(control.get('alternateInputIds')?.value || [])];
      if (control.get('val') && inputIds.includes(fieldId)) {
        return [control.get('val')];
      }
      children = Object.values(control.controls);
    } else {
      return [];
    }
    return children.reduce((found, child) => found.concat(this.findValueControls(child, fieldId)), []);
  }

  private hasValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    return typeof value === 'string' ? value.trim() !== '' : true;
  }

  /**
   * Localisation de l'erreur (prefixe|idInput) du premier contrôle rendu, pour ramener l'utilisateur sur la section « Modifier ».
   */
  private getLocation(controls: AbstractControl[]): string {
    const idInput = controls
      .map(control => (control as EformControl).idInput)
      .find(id => !!id && !id.includes('undefined') && !id.startsWith('null|'));
    return idInput ?? null;
  }

  private getMessage(): string {
    const label = this.translateService.instant('eform-not-editable.field|name|' + EformChangeSectionService.CHANGED_SECTION_FIELD);
    return this.translateService.instant('eform-editable.changed-section-required', {label});
  }
}
